import { Link, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { moduleEnabled } from '../config/modules';
import {
  LayoutDashboard, Users, Bus, Ticket, Package,
  Luggage, BarChart3, UserCog, ScanLine, LogOut, X, Compass
} from 'lucide-react';

const roleLabel = { admin: 'Administrador', operador: 'Operador', financeiro: 'Financeiro' };

function NavItem({ to, icon: Icon, label, isActive, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group ${
        isActive
          ? 'bg-white text-[var(--brand-primary)] shadow-md font-bold'
          : 'text-blue-100/80 hover:bg-white/10 hover:text-white'
      }`}
    >
      <Icon className={`w-4 h-4 flex-shrink-0 transition-colors ${isActive ? 'text-[var(--brand-primary)]' : 'text-blue-200/70 group-hover:text-white'}`} />
      <span>{label}</span>
    </Link>
  );
}

export default function Sidebar({ onClose }) {
  const { tenantSlug } = useParams();
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const { branding } = useTenant();

  const initials = user?.nome?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || 'U';
  const sidebarColor = branding?.corSidebar || '#001e45';
  const primaryColor = branding?.corPrimaria || '#0147a0';
  const secondaryColor = branding?.corSecundaria || '#d72d21';
  const isGradient = typeof sidebarColor === 'string' && sidebarColor.includes('gradient');
  const sidebarBg = isGradient
    ? sidebarColor
    : `linear-gradient(180deg, ${primaryColor} 0%, ${sidebarColor} 60%, ${sidebarColor} 100%)`;
  const nomeDoSistema = branding?.nomeDoSistema || 'Sistema Tour';

  const isActive = (path) => pathname.includes(`/${tenantSlug}/${path}`);

  return (
    <aside
      className="w-64 h-screen flex flex-col shadow-2xl"
      style={{ background: sidebarBg }}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          {branding?.logo ? (
            <img src={branding.logo} alt={nomeDoSistema} className="w-10 h-10 rounded-xl object-cover flex-shrink-0 shadow-md" />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-white font-bold text-lg shadow-md flex-shrink-0">
              {nomeDoSistema[0]}
            </div>
          )}
          <div className="leading-none">
            <p className="text-white font-bold text-sm tracking-tight">{nomeDoSistema}</p>
            <p className="text-[10px] font-semibold tracking-widest uppercase" style={{ color: '#f9c900' }}>Sistema</p>
          </div>
        </div>
        <button onClick={onClose} className="lg:hidden text-white/50 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-5 px-3 space-y-0.5 overflow-y-auto">
        <p className="text-blue-200/60 text-[10px] font-bold uppercase tracking-widest px-3 mb-3">Menu Principal</p>

        <NavItem to={`/${tenantSlug}/dashboard`} icon={LayoutDashboard} label="Dashboard" isActive={isActive('dashboard')} onClick={onClose} />
        <NavItem to={`/${tenantSlug}/clientes`} icon={Users} label="Clientes" isActive={isActive('clientes')} onClick={onClose} />
        {moduleEnabled(branding, 'guias') && (
          <NavItem to={`/${tenantSlug}/guias`} icon={Compass} label="Guias" isActive={isActive('guias')} onClick={onClose} />
        )}
        <NavItem to={`/${tenantSlug}/viagens`} icon={Bus} label="Viagens" isActive={isActive('viagens')} onClick={onClose} />
        <NavItem to={`/${tenantSlug}/reservas`} icon={Ticket} label="Reservas" isActive={isActive('reservas')} onClick={onClose} />
        {moduleEnabled(branding, 'bagagem') && (
          <NavItem to={`/${tenantSlug}/bagagens`} icon={Luggage} label="Bagagem" isActive={isActive('bagagens')} onClick={onClose} />
        )}
        {moduleEnabled(branding, 'encomendas') && (
          <NavItem to={`/${tenantSlug}/encomendas`} icon={Package} label="Encomendas" isActive={isActive('encomendas')} onClick={onClose} />
        )}

        {['admin', 'financeiro'].includes(user?.role) && (
          <>
            <div className="h-px bg-white/10 my-4 mx-1" />
            <p className="text-blue-200/60 text-[10px] font-bold uppercase tracking-widest px-3 mb-3">Análises</p>
            <NavItem to={`/${tenantSlug}/relatorios`} icon={BarChart3} label="Relatórios" isActive={isActive('relatorios')} onClick={onClose} />
          </>
        )}

        {['admin', 'operador'].includes(user?.role) && moduleEnabled(branding, 'qr') && (
          <>
            <div className="h-px bg-white/10 my-4 mx-1" />
            <p className="text-blue-200/60 text-[10px] font-bold uppercase tracking-widest px-3 mb-3">Operações</p>
            <NavItem to={`/${tenantSlug}/leitor-qr`} icon={ScanLine} label="Leitor QR Code" isActive={isActive('leitor-qr')} onClick={onClose} />
          </>
        )}

        {user?.role === 'admin' && (
          <>
            <div className="h-px bg-white/10 my-4 mx-1" />
            <p className="text-blue-200/60 text-[10px] font-bold uppercase tracking-widest px-3 mb-3">Administração</p>
            <NavItem to={`/${tenantSlug}/usuarios`} icon={UserCog} label="Usuários" isActive={isActive('usuarios')} onClick={onClose} />
          </>
        )}
      </nav>

      {/* User info */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3 px-1">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ring-2 ring-white/20"
            style={{ background: `linear-gradient(135deg, ${secondaryColor}, ${secondaryColor}cc)` }}
          >
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-white text-sm font-semibold truncate">{user?.nome || 'Usuário'}</p>
            <p className="text-blue-200/60 text-xs">{roleLabel[user?.role] || user?.role}</p>
          </div>
        </div>
        <button
          onClick={() => logout(tenantSlug)}
          className="flex items-center gap-2 text-blue-100/60 hover:text-white text-xs w-full py-2 px-3 rounded-lg hover:bg-white/10 transition-all duration-200 group"
        >
          <LogOut className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          Sair da conta
        </button>
      </div>
    </aside>
  );
}
